import { ChangeDetectionStrategy, Component, computed, inject, Input, signal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { finalize } from 'rxjs';
import { DeviceEnum, LanguageService, LoaderService, SearchItems, ToastService } from '../../core';
import { deviceListFilter } from '../../models';
import { ListService, PlanService, UserService } from '../../services';
import { BaseComponent, ReplacePipe } from '../../shared';


/**
 * Modal component to upgrade plan.
 */
@Component({
	selector: 'app-plan-upgrade-modal',
	imports: [
		ReplacePipe,
		FormsModule,
		ReactiveFormsModule
	],
	templateUrl: './plan-upgrade-modal.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class PlanUpgradeModalComponent extends BaseComponent {

	public activeModal = inject(NgbActiveModal);
	private readonly userSrv = inject(UserService);
	private readonly listSrv = inject(ListService);
	private readonly planSrv = inject(PlanService);
	private readonly toastSrv = inject(ToastService);
	private readonly loaderSrv = inject(LoaderService);
	protected override readonly langSrv = inject(LanguageService);

	@Input() planGuid: string = '';
	@Input() set device(val: DeviceEnum | undefined | null) {
		this.isDeviceVisible = !val || val === DeviceEnum.ToBeDefined;
		this.isDeviceVisible && this.form.controls.device.addValidators(Validators.required);
	};

	item = signal<SearchItems | null>(null);
	private readonly hasProd = this.userSrv.hasProduct();
	deviceList = computed(() => {
		return deviceListFilter(this.item(), this.hasProd?.jps, this.hasProd?.fitbone, this.hasProd?.tlhex)
	});

	isSaving = false;
	isDeviceVisible = false;

	private readonly SPECIAL_CHARS_LITE_PATTERN: RegExp = /^[^<>%#?]+$/;
	readonly SPECIAL_CHARS_LITE_TEXT = '<>%#?';
	readonly NOTES_MAXLENGTH = 1000;
	readonly form = new FormGroup({
		'terms': new FormControl<boolean>(false, [Validators.requiredTrue]),
		'device': new FormControl<DeviceEnum | null>(null),
		'notes': new FormControl<string | null>(null, [Validators.maxLength(this.NOTES_MAXLENGTH), Validators.pattern(this.SPECIAL_CHARS_LITE_PATTERN)])
	});

	/**
	 * Confirm upgrade.
	 */
	confirm(): void {
		if (this.form.valid && !this.isSaving) {
			this.isSaving = true;
			this.loaderSrv.show(true);
			this.planSrv.migratePlan(this.planGuid, this.form.value.device, this.form.value.notes).pipe(
				finalize(() => {
					this.isSaving = false;
					this.loaderSrv.hide();
				})
			).subscribe({
				next: () => {
					this.toastSrv.showSuccess(this.labels().PLAN_UPGRADE_MODAL_COMPONENT_UPGRADE_SUCCESS);
					this.activeModal.close();
					this.listSrv.search();
				},
				error: err => this.handleMigrateError(err)
			});
		}
	}

	private handleMigrateError(err: Error): void {
		this.toastSrv.showError(this.labels().PLAN_UPGRADE_MODAL_COMPONENT_UPGRADE_ERROR);
		if (this.isDeviceVisible && err.message === 'UnsupportedModel') {
			this.form.controls.device.setErrors({ 'unsupportedModel': true });
		}
	}

}
